import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import Modal from './Modal';
import {
  Folder, FolderOpen, FileText, Loader2, CheckCircle2, XCircle, AlertTriangle,
  CloudDownload, RefreshCw, ScanText, Sparkles, Database, Download, MinusCircle, RotateCcw,
} from 'lucide-react';

const STEP_ICONS = {
  download: Download,
  extract: FileText,
  ocr: ScanText,
  summary: Sparkles,
  index: Database,
};

const STATUS_CHIP = {
  ingested: { cls: 'v-badge conf', label: 'Ingested' },
  new: { cls: 'v-badge ai', label: 'New' },
  changed: { cls: 'v-badge cost', label: 'Update available' },
  failed: { cls: 'v-badge cost', label: 'Failed' },
  unassigned: { cls: 'v-badge cost', label: 'No department mapping' },
  unsupported: { cls: 'v-badge cost', label: 'Unsupported' },
};

export default function StorageSyncModal({ onClose, onSynced }) {
  const [browse, setBrowse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFolders, setOpenFolders] = useState({});
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);
  const sseRef = useRef(null);

  const loadBrowse = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/storage/browse`);
      setBrowse(data);
      setOpenFolders(prev => {
        if (Object.keys(prev).length) return prev;
        const first = Object.keys(data.folders)[0];
        return first ? { [first]: true } : {};
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reach storage backend');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBrowse(); }, [loadBrowse]);
  useEffect(() => () => { clearInterval(pollRef.current); sseRef.current?.close(); }, []);

  const startPollingFallback = (jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const { data: j } = await axios.get(`${API}/storage/sync/${jobId}`);
        setJob(j);
        if (j.status === 'done') {
          clearInterval(pollRef.current);
          loadBrowse();
          onSynced?.();
        }
      } catch { /* keep polling */ }
    }, 1500);
  };

  const startSync = async (paths) => {
    try {
      const { data } = await axios.post(`${API}/storage/sync`, { paths });
      if (!data.job_id) {
        toast.info(data.message || 'Nothing to process');
        return;
      }
      toast.success(`Sync started · ${data.files} document(s)`);
      const token = JSON.parse(localStorage.getItem('vedanta.auth') || '{}')?.token;
      const es = new EventSource(`${API}/storage/sync/${data.job_id}/stream?token=${encodeURIComponent(token)}`);
      sseRef.current = es;
      es.onmessage = (ev) => {
        const j = JSON.parse(ev.data);
        setJob(j);
        if (j.status === 'done') {
          es.close();
          loadBrowse();
          onSynced?.();
        }
      };
      es.onerror = () => {
        es.close();
        startPollingFallback(data.job_id);
      };
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed to start');
    }
  };

  const jobFiles = job ? Object.values(job.files) : [];
  const stepsCatalog = job?.steps_catalog || browse?.steps || [];
  const counts = browse?.counts || {};

  return (
    <Modal onClose={onClose} testid="storage-sync-modal" widthClass="max-w-3xl">
      <div className="px-8 pt-7 pb-4 border-b border-white/10 flex items-start justify-between">
        <div>
          <div className="data-label accent-line text-[#DDA079] mb-2">Azure Blob · {browse?.container || '…'}</div>
          <h2 className="font-headings text-2xl text-white">Sync from storage</h2>
          <p className="text-slate-400 text-[12px] mt-1">
            Folder decides the department (see mapping chips). Changed files are re-processed and their
            cached answers &amp; FAQs invalidated. One file failing never stops the batch.
          </p>
        </div>
        <button data-testid="storage-refresh" onClick={loadBrowse} className="btn-secondary flex items-center gap-1.5">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-8 py-5 space-y-5">
        {job && (
          <div className="border border-[#C17F59]/30 bg-[#C17F59]/[0.04] rounded-sm p-4" data-testid="sync-progress">
            <div className="flex items-center gap-2 mb-3">
              {job.status === 'running'
                ? <Loader2 size={14} className="animate-spin text-[#C17F59]" />
                : <CheckCircle2 size={14} className="text-emerald-400" />}
              <span className="data-label text-[#DDA079]">
                {job.status === 'running'
                  ? `Ingesting live… (document ${jobFiles.filter(f => ['done', 'updated', 'error'].includes(f.status)).length + 1} of ${jobFiles.length})`
                  : 'Sync complete'}
              </span>
            </div>
            {job.message && (
              <div className="text-[12px] text-emerald-300 font-mono mb-3" data-testid="sync-summary">{job.message}</div>
            )}
            <div className="space-y-3">
              {jobFiles.map(f => (
                <div key={f.path} className="border border-white/5 rounded-sm p-3" data-testid={`sync-file-${f.name}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[12px] text-white truncate pr-3">
                      {f.name}
                      <span className="text-slate-500 font-mono text-[10px] ml-2">→ {f.department}</span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {f.status === 'running' && <Loader2 size={12} className="animate-spin text-[#C17F59]" />}
                      {(f.status === 'done' || f.status === 'updated') && <CheckCircle2 size={12} className="text-emerald-400" />}
                      {f.status === 'error' && <XCircle size={12} className="text-red-400" />}
                      {f.status === 'queued' && <span className="text-[10px] text-slate-500 font-mono">QUEUED</span>}
                      {f.status === 'updated' && <span className="v-badge conf">Updated</span>}
                      {f.ocr_used && <span className="v-badge ai">OCR</span>}
                      {f.language && f.language !== 'en' && <span className="v-badge conf">{f.language.toUpperCase()}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    {stepsCatalog.map(s => {
                      const state = f.steps?.[s.id];
                      const Icon = STEP_ICONS[s.id] || FileText;
                      const active = f.step === s.id && f.status === 'running';
                      return (
                        <div
                          key={s.id}
                          className={`flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-mono uppercase tracking-wide border ${
                            state === 'done' ? 'border-emerald-500/30 text-emerald-300'
                            : state === 'skipped' ? 'border-white/5 text-slate-600'
                            : active ? 'border-[#C17F59]/50 text-[#DDA079]'
                            : 'border-white/5 text-slate-500'
                          }`}
                        >
                          {state === 'done' ? <CheckCircle2 size={10} />
                            : state === 'skipped' ? <MinusCircle size={10} />
                            : active ? <Loader2 size={10} className="animate-spin" />
                            : <Icon size={10} />}
                          {s.label}
                        </div>
                      );
                    })}
                  </div>
                  {f.cascade && (f.cascade.cache_entries > 0 || f.cascade.faqs_reverted > 0) && (
                    <div className="text-[11px] text-amber-300 mt-2">
                      Invalidated {f.cascade.cache_entries} cached answer(s), reverted {f.cascade.faqs_reverted} FAQ(s) to pending.
                    </div>
                  )}
                  {f.error && <div className="text-[11px] text-red-400 mt-2">{f.error}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {loading && !browse ? (
          <div className="flex items-center gap-2 text-slate-400 text-[13px] py-8 justify-center">
            <Loader2 size={14} className="animate-spin" /> Listing blob container…
          </div>
        ) : browse ? (
          <>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-[12px] text-slate-400 font-mono" data-testid="browse-counts">
                {browse.total} objects · <span className="text-emerald-400">{counts.ingested || 0} up to date</span>
                {' · '}<span className="text-[#DDA079]">{counts.new || 0} new</span>
                {counts.changed > 0 && <> · <span className="text-amber-400">{counts.changed} changed</span></>}
                {counts.failed > 0 && <> · <span className="text-red-400">{counts.failed} failed</span></>}
                {counts.unassigned > 0 && <> · <span className="text-slate-500">{counts.unassigned} unassigned</span></>}
              </div>
              <button
                data-testid="sync-all-new"
                disabled={!browse.actionable || job?.status === 'running'}
                onClick={() => startSync(null)}
                className="btn-primary flex items-center gap-2 disabled:opacity-40"
              >
                <CloudDownload size={13} /> Sync all ({browse.actionable})
              </button>
            </div>

            <div className="space-y-2">
              {Object.entries(browse.folders).map(([folder, files]) => {
                const open = openFolders[folder];
                const dept = files[0]?.department || browse.folder_department_map?.[folder];
                const nActionable = files.filter(f => ['new', 'changed', 'failed'].includes(f.status)).length;
                return (
                  <div key={folder} className="border border-white/10 rounded-sm overflow-hidden">
                    <button
                      data-testid={`folder-${folder}`}
                      onClick={() => setOpenFolders(o => ({ ...o, [folder]: !o[folder] }))}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.03] transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        {open ? <FolderOpen size={14} className="text-[#DDA079]" /> : <Folder size={14} className="text-[#C17F59]" />}
                        <span className="text-white text-[13px]">{folder}</span>
                        {dept
                          ? <span className="v-badge conf">→ {dept}</span>
                          : <span className="v-badge cost flex items-center gap-1"><AlertTriangle size={9} /> unmapped</span>}
                        <span className="text-[10px] text-slate-500 font-mono">{files.length} files</span>
                      </div>
                      {nActionable > 0 && <span className="v-badge ai">{nActionable} to sync</span>}
                    </button>
                    {open && (
                      <div className="border-t border-white/5">
                        {files.map(f => {
                          const chip = STATUS_CHIP[f.status] || STATUS_CHIP.unsupported;
                          return (
                            <div key={f.path} className="flex items-center justify-between px-4 py-2 border-b border-white/[0.03] last:border-0 hover:bg-white/[0.02]" data-testid={`blob-file-${f.name}`}>
                              <div className="flex items-center gap-2 min-w-0">
                                <FileText size={12} className="text-slate-500 flex-shrink-0" />
                                <span className="text-[12px] text-slate-200 truncate">{f.path.split('/').slice(1).join('/') || f.name}</span>
                                <span className="text-[10px] text-slate-600 font-mono flex-shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                                <span className={chip.cls} title={f.error || ''}>
                                  {f.status === 'ingested' && <CheckCircle2 size={10} className="inline mr-0.5" />}
                                  {chip.label}
                                </span>
                                {['new', 'changed', 'failed'].includes(f.status) && (
                                  <button
                                    data-testid={`sync-one-${f.name}`}
                                    disabled={job?.status === 'running'}
                                    onClick={() => startSync([f.path])}
                                    className="btn-secondary text-[10px] flex items-center gap-1 disabled:opacity-40"
                                  >
                                    {f.status === 'failed' ? <RotateCcw size={11} /> : <CloudDownload size={11} />}
                                    {f.status === 'failed' ? 'Retry' : f.status === 'changed' ? 'Update' : 'Sync'}
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="text-slate-400 text-[13px] py-8 text-center">Storage backend unreachable.</div>
        )}
      </div>

      <div className="px-8 py-4 border-t border-white/10 flex justify-end">
        <button onClick={onClose} className="btn-secondary" data-testid="storage-close">Close</button>
      </div>
    </Modal>
  );
}
